
'use client';
import { useState, useEffect, useMemo } from 'react';
import type {
  DocumentReference,
  DocumentData,
  DocumentSnapshot,
} from 'firebase/firestore';
import { onSnapshot } from 'firebase/firestore';

interface UseDoc<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export function useDoc<T extends DocumentData>(
  ref: DocumentReference<T> | null
): UseDoc<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Extract document info for better error logging
  const docInfo = useMemo(() => {
    if (!ref) return null;
    
    try {
      const path = ref.path || 'unknown';
      return `Document: ${path}`;
    } catch (e) {
      return 'Document path unavailable';
    }
  }, [ref]);

  useEffect(() => {
    if (!ref) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let mounted = true;

    const unsubscribe = onSnapshot(
      ref,
      (snapshot: DocumentSnapshot<T>) => {
        if (!mounted) return;
        
        if (snapshot.exists()) {
          const newData = { ...snapshot.data(), id: snapshot.id } as T;
          setData(prevData => {
            // Only update if data has actually changed
            if (JSON.stringify(prevData) === JSON.stringify(newData)) {
              return prevData;
            }
            return newData;
          });
        } else {
          // Document doesn't exist, which can be a normal case.
          // Consider if this should be an error or just null data.
          setData(null);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        if (!mounted) return;
        console.error(`Firestore 'useDoc' Error [${docInfo}]:`, err);
        console.error('Document details:', {
          path: ref.path,
          id: ref.id,
        });
        setError(err);
        setData(null);
        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [ref, docInfo]);

  return { data, loading, error };
}
